export type MedcallImlaiCountyHoliday = {
  date?: string | null;
  holiday?: string | null;
};

export type MedcallImlaiMedicineDetail = {
  display_name?: string | null;
  name?: string | null;
};

export type MedcallImlaiInjectionInfo = {
  date_due?: string | null;
};

export type MedcallImlaiDueDateMedication = {
  active?: boolean | number | string | null;
  days_of_week?: Array<string | null> | null;
  frequency?: string | null;
  id: number;
  injection_info?: MedcallImlaiInjectionInfo[] | null;
  medicine_detail?: MedcallImlaiMedicineDetail[] | MedcallImlaiMedicineDetail | null;
  medicine_name?: string | null;
  route?: string | null;
  strength?: string | null;
  times_per_day?: string | null;
};

export type MedcallImlaiDueDateWarningItem = {
  dueDate: string;
  holidayName: string | null;
  isWeekend: boolean;
  medication: MedcallImlaiDueDateMedication;
};

export type MedcallImlaiDueDateUpdate = {
  dueDate: string;
  medication: MedcallImlaiDueDateMedication;
  medicationId: number;
  previousDueDate: string;
};

export type MedcallImlaiDueDateDrafts = Record<number, string>;

const DOSE_NAME_PATTERN =
  /^\s*\d+(?:\.\d+)?\s*(?:mg|mcg|g|kg|ml|mL|l|L|unit|units)?(?:\s*\/\s*(?:\d+(?:\.\d+)?\s*)?(?:mg|mcg|g|kg|ml|mL|l|L|unit|units))?\s*$/i;
const DOSE_SUFFIX_PATTERN =
  /(\d+(?:\.\d+)?\s*(?:mg|mcg|g|kg|ml|mL|l|L|unit|units)(?:\s*\/\s*(?:\d+(?:\.\d+)?\s*)?(?:mg|mcg|g|kg|ml|mL|l|L|unit|units))?)\s*$/i;
const NUMERIC_ONLY_PATTERN = /^\s*\d+(?:\.\d+)?\s*$/;

const normalizeText = (value: unknown) => `${value ?? ''}`.trim();
const padDatePart = (value: number) => `${value}`.padStart(2, '0');

export const normalizeMedcallImlaiDate = (value: unknown) => {
  const match = normalizeText(value).match(/^(\d{4})-(\d{2})-(\d{2})(?:$|[T\s])/);
  if (!match) {
    return null;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const parsedDate = new Date(Date.UTC(year, month - 1, day));
  if (
    parsedDate.getUTCFullYear() !== year
    || parsedDate.getUTCMonth() !== month - 1
    || parsedDate.getUTCDate() !== day
  ) {
    return null;
  }

  return `${match[1]}-${match[2]}-${match[3]}`;
};

export const getMedcallImlaiDateLowerBound = (now = new Date()) => {
  return [
    now.getFullYear(),
    padDatePart(now.getMonth() + 1),
    padDatePart(now.getDate()),
  ].join('-');
};

export const formatMedcallImlaiLegacyDate = (value: unknown) => {
  const normalizedDate = normalizeMedcallImlaiDate(value);
  if (!normalizedDate) {
    return '';
  }

  const [year, month, day] = normalizedDate.split('-');
  return `${month}/${day}/${year.slice(-2)}`;
};

const isWeekendDate = (value: string) => {
  const [year, month, day] = value.split('-').map(Number);
  const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  return weekday === 0 || weekday === 6;
};

export const getMedcallImlaiDueDateWarnings = (
  medications: MedcallImlaiDueDateMedication[],
  countyHolidays: MedcallImlaiCountyHoliday[],
  today: string,
) => {
  const normalizedToday = normalizeMedcallImlaiDate(today);
  if (!normalizedToday) {
    return [];
  }

  const holidaysByDate = new Map<string, MedcallImlaiCountyHoliday>();
  countyHolidays.forEach((holiday) => {
    const holidayDate = normalizeMedcallImlaiDate(holiday.date);
    if (!holidayDate || holidaysByDate.has(holidayDate)) {
      return;
    }

    holidaysByDate.set(holidayDate, holiday);
  });

  return medications.reduce<MedcallImlaiDueDateWarningItem[]>((warnings, medication) => {
    if (normalizeText(medication.route) !== 'IM-LAI') {
      return warnings;
    }

    const dueDate = normalizeMedcallImlaiDate(medication.injection_info?.[0]?.date_due);
    if (!dueDate || dueDate <= normalizedToday) {
      return warnings;
    }

    const isWeekend = isWeekendDate(dueDate);
    const holiday = holidaysByDate.get(dueDate) || null;
    if (!isWeekend && !holiday) {
      return warnings;
    }

    warnings.push({
      dueDate,
      holidayName: normalizeText(holiday?.holiday) || null,
      isWeekend,
      medication,
    });
    return warnings;
  }, []);
};

export const getMedcallImlaiDueDateReasonLabel = (warning: MedcallImlaiDueDateWarningItem) => {
  const formattedDate = formatMedcallImlaiLegacyDate(warning.dueDate);
  if (warning.isWeekend) {
    return `Weekend: ${formattedDate}`;
  }

  return `${warning.holidayName || 'Holiday'}: ${formattedDate}`;
};

const getFirstMedicineDetail = (medication: MedcallImlaiDueDateMedication) => {
  if (Array.isArray(medication.medicine_detail)) {
    return medication.medicine_detail[0] || null;
  }

  return medication.medicine_detail || null;
};

export const getMedcallImlaiMedicationName = (medication: MedcallImlaiDueDateMedication) => {
  const medicineName = normalizeText(medication.medicine_name);
  const medicineDetail = getFirstMedicineDetail(medication);
  const detailName = normalizeText(medicineDetail?.display_name || medicineDetail?.name);
  if (DOSE_NAME_PATTERN.test(medicineName) && detailName) {
    return detailName;
  }

  return medicineName || detailName;
};

export const getMedcallImlaiMedicationDose = (medication: MedcallImlaiDueDateMedication) => {
  const medicineName = normalizeText(medication.medicine_name);
  const strength = normalizeText(medication.strength);
  if (DOSE_NAME_PATTERN.test(medicineName) && NUMERIC_ONLY_PATTERN.test(strength)) {
    return medicineName;
  }

  const doseMatch = medicineName.match(DOSE_SUFFIX_PATTERN);
  const dose = normalizeText(doseMatch?.[1]).replace(/\s+/g, ' ');
  const doseNumber = dose.match(/^\d+(?:\.\d+)?/);
  if (doseNumber && Number(doseNumber[0]) === Number(strength)) {
    return dose;
  }

  return strength || medicineName;
};

export const getMedcallImlaiMedicationFrequency = (medication: MedcallImlaiDueDateMedication) => {
  const frequency = normalizeText(medication.frequency);
  if (frequency === 'everyotherday') {
    return 'QOD';
  }

  if (frequency === 'custom_days') {
    const days = (medication.days_of_week || []).filter(Boolean).join(', ');
    return `${days} - ${normalizeText(medication.times_per_day)}`.toUpperCase();
  }

  if (frequency === 'everyday') {
    return normalizeText(medication.times_per_day).toUpperCase();
  }

  return frequency.toUpperCase();
};

export const createMedcallImlaiDueDateDrafts = (
  warnings: MedcallImlaiDueDateWarningItem[],
) => {
  return warnings.reduce<MedcallImlaiDueDateDrafts>((drafts, warning) => {
    drafts[warning.medication.id] = warning.dueDate;
    return drafts;
  }, {});
};

export const createMedcallImlaiDueDateUpdate = (
  warning: MedcallImlaiDueDateWarningItem,
  dueDate: string,
): MedcallImlaiDueDateUpdate => {
  return {
    dueDate,
    medication: warning.medication,
    medicationId: warning.medication.id,
    previousDueDate: warning.dueDate,
  };
};

export const createMedcallImlaiDueDateUpdates = (
  warnings: MedcallImlaiDueDateWarningItem[],
  drafts: MedcallImlaiDueDateDrafts,
) => {
  return warnings.map((warning) => {
    return createMedcallImlaiDueDateUpdate(
      warning,
      drafts[warning.medication.id] || warning.dueDate,
    );
  });
};
